import type { HubConnection } from '@microsoft/signalr'
import { getRealtimeConnectionId } from './realtimeIdentity'

/**
 * Settings changed on another device — the receiving half.
 *
 * The server tells every device signed in as this user which area of their settings moved, so a
 * change made on a phone shows up in a browser tab left open on the settings page, and the other
 * way round. This module turns that hub event into something React can subscribe to.
 *
 * ## Why the event carries no values
 *
 * It says *what area* changed, never what it changed to. A settings screen is a form, and pushing
 * values into a form means deciding what to do about fields somebody is part-way through editing.
 * Naming the area lets each screen decide for itself: refetch when nothing is in flight, and say so
 * rather than overwrite when something is. It also means a new setting inside an existing area
 * synchronises with no client change at all.
 *
 * ## Echo suppression
 *
 * The device that made the change is told too — the server sends to the user, not to a device. That
 * device already has the new values, so reloading would blank and repopulate a form under the hand
 * that just saved it, which reads as the save being undone and redone. Every request carries this
 * tab's connection id, the server sends it back on the event, and the match is dropped here.
 *
 * When the hub is not connected there is no id to match, and no event either — the tab is not
 * listening, and picks up the change on its next ordinary fetch.
 */

/** Mirrors the server's SettingsScopes. Renaming one on either side silently stops that sync. */
export const SETTINGS_SCOPES = {
  account: 'account',
  profile: 'profile',
  notifications: 'notifications',
  journalSound: 'journal-sound',
  adultContent: 'adult-content',
  privacy: 'privacy',
  security: 'security',
  sessions: 'sessions',
  homeLocation: 'home-location',
  /** Base Camp widget layouts (Backlog 046 §5.1). */
  dashboardLayout: 'dashboard-layout',
  /** The adventure editor's menu style (Full / Simplified). */
  editor: 'editor',
} as const

export type SettingsScope = (typeof SETTINGS_SCOPES)[keyof typeof SETTINGS_SCOPES]

type Listener = (scope: SettingsScope) => void

const listeners = new Set<Listener>()

/**
 * Listen for changes. Returns its own unsubscribe.
 *
 * Module-level rather than per-connection, so a component can subscribe before the hub is up and
 * keep its subscription across a reconnect — neither of which it can see.
 */
export function onSettingsChanged(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Wire a freshly built hub connection to the bus above.
 *
 * Called once per connection, right after it is created. SignalR keeps handlers registered with
 * `.on` across its own automatic reconnects, so this does not need repeating when the network
 * drops — only when a new connection object is built.
 */
export function attachSettingsSync(connection: HubConnection) {
  connection.on('SettingsChanged', (event: unknown) => {
    const payload = event as Record<string, unknown> | null
    if (!payload) return

    // ASP.NET's default serializer preserves PascalCase; some paths hand back camelCase. Both are
    // accepted for the same reason the session handlers accept both — guessing wrong here fails
    // silently, as a sync that simply never happens.
    const scope = (payload.Scope ?? payload.scope) as SettingsScope | undefined
    const origin = (payload.OriginConnectionId ?? payload.originConnectionId) as string | null | undefined
    if (!scope) return

    const own = getRealtimeConnectionId()
    if (origin && own && origin === own) return

    listeners.forEach((listener) => {
      try {
        listener(scope)
      } catch (err) {
        // One screen's refresh throwing must not stop the others being told.
        console.error('[settingsSync] listener failed for scope', scope, err)
      }
    })
  })
}
